import type { LabelKind, LabelSizeId } from "../types";
import { extractPlaceholders } from "../zpl/helpers";
import {
  getShippedLabelTemplate,
  getShippedLabelTemplateByKey,
  getShippedZplBody,
  isLabelTemplateKey,
  labelTemplateKey,
  placeholdersForKind,
  type LabelTemplateKey,
} from "../zpl/shippedTemplates";
import { DEFAULT_LABEL_SIZE } from "../zpl/sizes";

export const LABEL_ZPL_TEMPLATES_SETTING_KEY = "label_zpl_templates";

/** Company overrides keyed by `kind:size` (e.g. `spare-part:76x51`). Missing keys use shipped defaults. */
export type CompanyLabelZplTemplates = Partial<Record<LabelTemplateKey, string>>;

export const DEFAULT_COMPANY_LABEL_ZPL_TEMPLATES: CompanyLabelZplTemplates = {};

/**
 * Parse `company_settings.setting_value` for label_zpl_templates.
 * Only known shipped keys with non-empty string bodies are kept. Never throws.
 */
export function parseCompanyLabelZplTemplates(raw: unknown): CompanyLabelZplTemplates {
  let value: unknown = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const entries = Object.entries(value as Record<string, unknown>);
  const out: CompanyLabelZplTemplates = {};
  // Canonical keys first, then legacy "bin:<size>" fills "storage-bin:<size>" only when unset.
  const ordered = [
    ...entries.filter(([k]) => !k.startsWith("bin:")),
    ...entries.filter(([k]) => k.startsWith("bin:")).map(([k, v]) => [`storage-${k}`, v] as const),
  ];
  for (const [key, body] of ordered) {
    if (!isLabelTemplateKey(key)) continue;
    if (out[key]) continue;
    if (typeof body !== "string") continue;
    const trimmed = body.trim();
    if (!trimmed) continue;
    out[key] = body;
  }
  return out;
}

/** Resolve effective ZPL body: company override if present, otherwise shipped default. */
export function resolveZplTemplate(
  kind: LabelKind,
  size: LabelSizeId,
  overrides?: CompanyLabelZplTemplates | null,
): string {
  const key = labelTemplateKey(kind, size);
  const override = overrides?.[key];
  if (typeof override === "string" && override.trim().length > 0) {
    return override;
  }
  const shipped = getShippedZplBody(kind, size);
  if (shipped) return shipped;
  // Last resort: any shipped template for this kind (size fallback).
  const fallback = getShippedLabelTemplate(kind, DEFAULT_LABEL_SIZE);
  return fallback?.body ?? "";
}

export type ZplTemplateValidation = {
  ok: boolean;
  error?: string;
};

/** Validate an override body against the kind's placeholder allowlist and basic ZPL markers. */
export function validateZplTemplateOverride(
  kind: LabelKind,
  body: string,
): ZplTemplateValidation {
  const trimmed = body.trim();
  if (!trimmed) {
    return { ok: false, error: "Template is empty" };
  }
  if (!trimmed.includes("^XA")) {
    return { ok: false, error: "Template must include ^XA" };
  }
  if (!trimmed.includes("^XZ")) {
    return { ok: false, error: "Template must include ^XZ" };
  }
  const allowed = new Set(placeholdersForKind(kind));
  for (const name of extractPlaceholders(trimmed)) {
    if (!allowed.has(name)) {
      return { ok: false, error: `Unknown placeholder {{${name}}}` };
    }
  }
  return { ok: true };
}

/**
 * Keep only overrides that differ from the shipped default (trimmed).
 * Empty / identical bodies are dropped so the DB stays lean.
 */
export function serializeCompanyLabelZplTemplates(
  drafts: CompanyLabelZplTemplates,
): CompanyLabelZplTemplates {
  const out: CompanyLabelZplTemplates = {};
  for (const [key, body] of Object.entries(drafts) as [LabelTemplateKey, string | undefined][]) {
    if (typeof body !== "string") continue;
    if (!isLabelTemplateKey(key)) continue;
    const trimmed = body.trim();
    if (!trimmed) continue;
    const shipped = getShippedLabelTemplateByKey(key);
    if (shipped && trimmed === shipped.body.trim()) continue;
    const kind = shipped?.kind;
    if (!kind) continue;
    const validation = validateZplTemplateOverride(kind, body);
    if (!validation.ok) {
      throw new Error(`${shipped.label}: ${validation.error}`);
    }
    out[key] = body;
  }
  return out;
}

export { labelTemplateKey, type LabelTemplateKey };
